/**
 * DignityIntakeCollector — Conversational intake question definitions, validation,
 * quick-reply configs, and free-text parsing for the Dignity chat experience.
 *
 * Each "phase" maps to one intake question. Dignity asks the question,
 * the user responds via free-text or quick-reply button, and the collector
 * parses the response into structured IntakeData fields.
 */

import type { IntakeData } from './AiIntakeForm';

export type IntakePhase =
  | 'greeting'
  | 'urgency'
  | 'insurance'
  | 'location'
  | 'travel'
  | 'international'
  | 'detail'
  | 'summary';

export interface QuickReply {
  label: string;
  value: string;
}

export interface IntakeQuestion {
  phase: IntakePhase;
  getMessage: (partial: Partial<IntakeData>) => string;
  quickReplies?: (partial: Partial<IntakeData>) => QuickReply[];
  field: keyof IntakeData | null; // null for greeting/summary
  parse: (input: string, partial: Partial<IntakeData>) => Partial<IntakeData>;
  validate: (input: string) => boolean;
  inputType?: 'text' | 'zip'; // default 'text'
  skipButton?: string; // text for optional skip
}

// ── Keyword matching helpers ──

function matchesAny(input: string, keywords: string[]): boolean {
  const lower = input.toLowerCase();
  return keywords.some(k => lower.includes(k));
}

function parseInsurance(input: string): string {
  const lower = input.toLowerCase();
  if (matchesAny(lower, ['hmo'])) return 'insured_hmo';
  if (matchesAny(lower, ['ppo'])) return 'insured_ppo';
  if (matchesAny(lower, ['medicare'])) return 'medicare';
  if (matchesAny(lower, ['medicaid'])) return 'medicaid';
  if (matchesAny(lower, ['uninsured', 'no insurance', 'self-pay', 'self pay', 'cash'])) return 'uninsured';
  if (matchesAny(lower, ['not sure', 'skip', 'don\'t know', 'unsure', 'idk'])) return 'not_sure';
  if (matchesAny(lower, ['insured', 'insurance', 'blue cross', 'aetna', 'united', 'cigna', 'humana', 'anthem', 'kaiser'])) return 'insured_other';
  return 'not_sure';
}

function parseUrgency(input: string): string {
  const lower = input.toLowerCase();
  if (matchesAny(lower, ['emergency', 'urgent', 'immediate', 'asap', 'right now', 'er '])) return 'emergency';
  if (matchesAny(lower, ['scheduled', 'planned', 'date', 'appointment', 'booked'])) return 'scheduled';
  return 'exploring';
}

function parseTravelRadius(input: string): number {
  const lower = input.toLowerCase();
  if (matchesAny(lower, ['any', 'anywhere', 'doesn\'t matter', 'no limit'])) return 0;
  const numMatch = input.match(/(\d+)/);
  if (numMatch) {
    const n = parseInt(numMatch[1], 10);
    if (n <= 10) return 10;
    if (n <= 25) return 25;
    if (n <= 50) return 50;
    if (n <= 100) return 100;
    return 250;
  }
  if (matchesAny(lower, ['local', 'nearby', 'close'])) return 10;
  return 50;
}

function parseDetailLevel(input: string): string {
  const lower = input.toLowerCase();
  if (matchesAny(lower, ['quick', 'brief', 'overview', 'high-level', 'fast'])) return 'quick';
  if (matchesAny(lower, ['comprehensive', 'full', 'deep', 'detailed', 'everything', 'negotiation'])) return 'comprehensive';
  return 'standard';
}

function parseInternational(input: string): boolean {
  const lower = input.toLowerCase();
  if (matchesAny(lower, ['yes', 'sure', 'open', 'willing', 'mexico', 'canada', 'international', 'abroad'])) return true;
  return false;
}

// ── Border state detection ──

const BORDER_STATES: Record<string, { country: string; examples: string }> = {
  TX: { country: 'Mexico', examples: 'Juarez, Tijuana, or Monterrey' },
  AZ: { country: 'Mexico', examples: 'Nogales or Los Algodones' },
  NM: { country: 'Mexico', examples: 'Ciudad Juarez or Palomas' },
  CA: { country: 'Mexico', examples: 'Tijuana or Los Algodones' },
  WA: { country: 'Canada', examples: 'Vancouver or Victoria' },
  MT: { country: 'Canada', examples: 'Calgary or Lethbridge' },
  ND: { country: 'Canada', examples: 'Winnipeg or Regina' },
  MN: { country: 'Canada', examples: 'Winnipeg or Thunder Bay' },
  MI: { country: 'Canada', examples: 'Windsor or Toronto' },
  NY: { country: 'Canada', examples: 'Toronto or Montreal' },
  VT: { country: 'Canada', examples: 'Montreal or Sherbrooke' },
  ME: { country: 'Canada', examples: 'St. Stephen or Fredericton' },
  NH: { country: 'Canada', examples: 'Sherbrooke or Montreal' },
};

// Basic ZIP → state mapping (first 3 digits)
function zipToState(zip: string): string | null {
  if (!zip || zip.length < 3) return null;
  const prefix = parseInt(zip.substring(0, 3), 10);
  // Simplified mapping for border states
  if (prefix >= 750 && prefix <= 799) return 'TX';
  if (prefix >= 850 && prefix <= 865) return 'AZ';
  if (prefix >= 870 && prefix <= 884) return 'NM';
  if (prefix >= 900 && prefix <= 961) return 'CA';
  if (prefix >= 980 && prefix <= 994) return 'WA';
  if (prefix >= 590 && prefix <= 599) return 'MT';
  if (prefix >= 580 && prefix <= 588) return 'ND';
  if (prefix >= 550 && prefix <= 567) return 'MN';
  if (prefix >= 480 && prefix <= 499) return 'MI';
  if (prefix >= 100 && prefix <= 149) return 'NY';
  if (prefix >= 50 && prefix <= 59) return 'VT';
  if (prefix >= 39 && prefix <= 49) return 'ME';
  if (prefix >= 30 && prefix <= 38) return 'NH';
  return null;
}

export function getBorderInfo(zip: string): { country: string; examples: string } | null {
  const state = zipToState(zip);
  if (state && BORDER_STATES[state]) return BORDER_STATES[state];
  return null;
}

// ── Question definitions ──

export const INTAKE_QUESTIONS: IntakeQuestion[] = [
  // Phase 1: Greeting — collect situation
  {
    phase: 'greeting',
    getMessage: () =>
      `Hey there — I'm Dignity, your financial healthcare advisor. I'm here to help you understand exactly what your medical care will cost, with real pricing data from thousands of hospitals.\n\nTell me what's going on — what medical situation are you dealing with?`,
    quickReplies: () => [
      { label: 'Surgery', value: 'I need help pricing: Surgery' },
      { label: 'Emergency', value: 'I need help pricing: Emergency' },
      { label: 'Chronic Condition', value: 'I need help pricing: Chronic Condition' },
      { label: 'Pregnancy', value: 'I need help pricing: Pregnancy' },
      { label: 'Preventive', value: 'I need help pricing: Preventive' },
      { label: 'Cancer Treatment', value: 'I need help pricing: Cancer Treatment' },
    ],
    field: 'situation',
    parse: (input) => ({ situation: input.trim() }),
    validate: (input) => input.trim().length > 10,
  },

  // Phase 2: Urgency
  {
    phase: 'urgency',
    getMessage: (partial) => {
      const sit = partial.situation || '';
      const brief = sit.length > 60 ? sit.slice(0, 60).trim() + '...' : sit;
      return `Got it — "${brief}". I'll help you understand the full cost picture.\n\nHow urgent is this for you?`;
    },
    quickReplies: () => [
      { label: 'Emergency', value: 'emergency' },
      { label: 'Scheduled', value: 'scheduled' },
      { label: 'Just exploring costs', value: 'exploring' },
    ],
    field: 'urgency',
    parse: (input) => ({ urgency: parseUrgency(input) }),
    validate: () => true,
  },

  // Phase 3: Insurance
  {
    phase: 'insurance',
    getMessage: () =>
      `What's your insurance situation? This helps me give you the most relevant pricing.`,
    quickReplies: () => [
      { label: 'HMO', value: 'insured_hmo' },
      { label: 'PPO', value: 'insured_ppo' },
      { label: 'Other Insurance', value: 'insured_other' },
      { label: 'Uninsured', value: 'uninsured' },
      { label: 'Medicare', value: 'medicare' },
      { label: 'Medicaid', value: 'medicaid' },
    ],
    field: 'insurance',
    parse: (input) => ({ insurance: parseInsurance(input) }),
    validate: () => true,
    skipButton: 'Not sure / Skip',
  },

  // Phase 4: ZIP code
  {
    phase: 'location',
    getMessage: () =>
      `What's your ZIP code? I'll find hospitals near you with the best pricing.`,
    field: 'zipCode',
    inputType: 'zip',
    parse: (input) => ({ zipCode: input.replace(/\D/g, '').slice(0, 5) }),
    validate: (input) => {
      const digits = input.replace(/\D/g, '');
      return digits.length === 5 || digits.length === 0;
    },
    skipButton: 'Skip — show national prices',
  },

  // Phase 5: Travel radius
  {
    phase: 'travel',
    getMessage: () =>
      `How far are you willing to travel for a better deal?\n\nYour surgeries, anesthesia, and hospital stays always stay at the same place — I only shop around for portable things like labs, imaging, and blood work where prices vary wildly. (Some hospitals charge $4,500 for an MRI that costs $300 down the road.)`,
    quickReplies: () => [
      { label: 'Stay local — 10 mi', value: '10' },
      { label: '25 miles', value: '25' },
      { label: '50 miles', value: '50' },
      { label: '100 miles', value: '100' },
      { label: '250+ miles', value: '250' },
    ],
    field: 'travelRadius',
    parse: (input) => ({ travelRadius: parseTravelRadius(input) }),
    validate: () => true,
  },

  // Phase 6: International (conditional based on ZIP)
  {
    phase: 'international',
    getMessage: (partial) => {
      const zip = partial.zipCode || '';
      const border = getBorderInfo(zip);
      if (border) {
        return `Since you're near the ${border.country} border, would you consider getting care there? Many Americans save 50-80% on procedures — and places like ${border.examples} are just a short drive away.`;
      }
      return `Would you consider traveling abroad for major savings? Americans save 50-80% on procedures in countries like Mexico, Canada, and parts of Europe.`;
    },
    quickReplies: (partial) => {
      const zip = partial.zipCode || '';
      const border = getBorderInfo(zip);
      if (border) {
        return [
          { label: `Yes, show ${border.country} options`, value: 'yes' },
          { label: 'No, US only', value: 'no' },
        ];
      }
      return [
        { label: 'Yes, show international options', value: 'yes' },
        { label: 'No, keep it in the US', value: 'no' },
      ];
    },
    field: 'willingToTravelInternationally',
    parse: (input) => ({ willingToTravelInternationally: parseInternational(input) }),
    validate: () => true,
  },

  // Phase 7: Detail level
  {
    phase: 'detail',
    getMessage: () =>
      `Last thing — how detailed do you want your estimate?`,
    quickReplies: () => [
      { label: 'Quick overview', value: 'quick' },
      { label: 'Standard breakdown', value: 'standard' },
      { label: 'Full deep-dive + tips', value: 'comprehensive' },
    ],
    field: 'detailLevel',
    parse: (input) => ({ detailLevel: parseDetailLevel(input) }),
    validate: () => true,
  },

  // Phase 8: Summary (no input needed — transitions to payment)
  {
    phase: 'summary',
    getMessage: (partial) => {
      const parts: string[] = [];
      if (partial.situation) parts.push(`**Situation:** ${partial.situation}`);
      if (partial.urgency) {
        const labels: Record<string, string> = { emergency: 'Emergency', scheduled: 'Scheduled', exploring: 'Exploring options' };
        parts.push(`**Urgency:** ${labels[partial.urgency] || partial.urgency}`);
      }
      if (partial.insurance) {
        const labels: Record<string, string> = {
          insured_hmo: 'HMO', insured_ppo: 'PPO', insured_other: 'Other insurance',
          uninsured: 'Uninsured', medicare: 'Medicare', medicaid: 'Medicaid', not_sure: 'Not sure',
        };
        parts.push(`**Insurance:** ${labels[partial.insurance] || partial.insurance}`);
      }
      if (partial.zipCode) parts.push(`**Location:** ${partial.zipCode}`);
      if (partial.travelRadius !== undefined) {
        parts.push(`**Travel radius:** ${partial.travelRadius === 0 ? 'Any distance' : partial.travelRadius + ' miles'}`);
      }
      if (partial.willingToTravelInternationally) parts.push(`**International:** Yes`);

      return `Here's what I've got:\n\n${parts.join('\n')}\n\nI'm ready to build your personalized cost analysis with real hospital pricing data. To get started, I'll need a one-time $1.99 payment for the AI analysis.`;
    },
    field: null,
    parse: () => ({}),
    validate: () => true,
  },
];

// ── Get the next phase after current ──

const PHASE_ORDER: IntakePhase[] = [
  'greeting', 'urgency', 'insurance', 'location', 'travel', 'international', 'detail', 'summary',
];

export function getNextPhase(current: IntakePhase): IntakePhase | null {
  const idx = PHASE_ORDER.indexOf(current);
  if (idx === -1 || idx >= PHASE_ORDER.length - 1) return null;
  return PHASE_ORDER[idx + 1];
}

export function getQuestion(phase: IntakePhase): IntakeQuestion | undefined {
  return INTAKE_QUESTIONS.find(q => q.phase === phase);
}

// ── Build complete IntakeData from partial ──

export function buildIntakeData(partial: Partial<IntakeData>): IntakeData {
  return {
    situation: partial.situation || '',
    doctorNotes: partial.doctorNotes || '',
    insurance: partial.insurance || 'not_sure',
    zipCode: partial.zipCode || '',
    urgency: partial.urgency || 'exploring',
    travelRadius: partial.travelRadius ?? 50,
    willingToTravelInternationally: partial.willingToTravelInternationally ?? false,
    consolidation: partial.consolidation ?? 50,
    detailLevel: partial.detailLevel || 'standard',
  };
}
